"use client";

import { useMemo, useState } from "react";
import Link from "next/link";
import { useInvoiceStore } from "@/lib/invoice/store";
import {
  agingBucket,
  computeTotals,
  docSign,
  financialYear,
  fmtMoney,
  invoiceStatus,
  monthLabel,
  num,
  round2,
  todayISO,
} from "@/lib/invoice/utils";
import { Button, Card, Loading, PageHeader, Select, Tabs, useFeedback } from "@/components/Invoice/ui";

const TABS = [
  { value: "gst", label: "GST summary" },
  { value: "aging", label: "Receivables aging" },
  { value: "clients", label: "Revenue by client" },
  { value: "services", label: "Revenue by service" },
  { value: "projects", label: "Revenue by project" },
  { value: "payments", label: "Payments & TDS" },
];
const BUCKETS = ["current", "1-30", "31-60", "61-90", "90+"];

function Table({ columns, rows, foot }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="bg-slate-50/70 text-left text-[11px] font-semibold uppercase tracking-wider text-slate-500">
          <tr>
            {columns.map((c) => (
              <th key={c.key} className={`px-3 py-2.5 ${c.money ? "text-right" : ""}`}>{c.header}</th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {rows.length === 0 && (
            <tr>
              <td colSpan={columns.length} className="px-3 py-8 text-center text-slate-500">No data for this period.</td>
            </tr>
          )}
          {rows.map((r, i) => (
            <tr key={i} className="transition-colors hover:bg-slate-50/70">
              {columns.map((c) => (
                <td key={c.key} className={`px-3 py-2 ${c.money ? "text-right tabular-nums" : ""}`}>
                  {c.render ? c.render(r) : c.money ? (r[c.key] ? r[c.key].toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : "–") : r[c.key]}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
        {foot && (
          <tfoot className="border-t-2 border-slate-300 font-semibold">
            <tr>
              {columns.map((c, i) => (
                <td key={c.key} className={`px-3 py-2 ${c.money ? "text-right tabular-nums" : ""}`}>
                  {i === 0 ? "Total" : c.money ? round2(rows.reduce((s, r) => s + (r[c.key] || 0), 0)).toLocaleString("en-IN", { minimumFractionDigits: 2 }) : ""}
                </td>
              ))}
            </tr>
          </tfoot>
        )}
      </table>
    </div>
  );
}

export default function ReportsPage() {
  const store = useInvoiceStore();
  const { toast } = useFeedback();
  const [tab, setTab] = useState("gst");
  const [fy, setFy] = useState(financialYear(todayISO()));
  const [currency, setCurrency] = useState("");

  const fys = useMemo(() => {
    const s = new Set(store.invoices.map((i) => financialYear(i.date)));
    s.add(financialYear(todayISO()));
    return [...s].sort().reverse();
  }, [store.invoices]);

  const docs = useMemo(
    () =>
      store.invoices
        .filter((i) => ["invoice", "credit"].includes(i.docType || "invoice"))
        .map((inv) => {
          const t = computeTotals(inv);
          return { inv, t, status: invoiceStatus(inv, t), sign: docSign(inv) };
        })
        .filter((r) => r.status !== "draft" && r.status !== "cancelled"),
    [store.invoices]
  );
  const currencies = useMemo(() => {
    const s = new Set([store.settings.defaults.currency]);
    docs.forEach((d) => s.add(d.inv.currency));
    return [...s];
  }, [docs, store.settings]);
  const cur = currency || currencies[0];

  const inFy = (date) => fy === "all" || financialYear(date) === fy;

  // ---------- GST summary (always in INR; foreign invoices converted at their exchange rate) ----------
  const gst = useMemo(() => {
    const months = {};
    const missingFx = [];
    for (const { inv, t, sign } of docs) {
      if (!inFy(inv.date)) continue;
      const fx = inv.currency === "INR" ? 1 : num(inv.fxRate);
      if (!fx) missingFx.push(inv);
      const k = inv.date.slice(0, 7);
      const m = (months[k] ||= { month: monthLabel(k), key: k, b2b: 0, b2c: 0, export: 0, exportForeign: "", cgst: 0, sgst: 0, igst: 0, credit: 0, count: 0 });
      const taxable = t.subtotal - t.discount;
      m.count++;
      if (sign < 0) m.credit += taxable * (fx || 0);
      if (inv.taxMode === "export") m.export += sign * taxable * (fx || 0);
      else if (inv.billTo?.gstin) m.b2b += sign * taxable * fx;
      else m.b2c += sign * taxable * fx;
      m.cgst += sign * t.cgst * fx;
      m.sgst += sign * t.sgst * fx;
      m.igst += sign * t.igst * fx;
    }
    return { rows: Object.values(months).sort((a, b) => a.key.localeCompare(b.key)).map((r) => ({ ...r, tax: r.cgst + r.sgst + r.igst })), missingFx };
  }, [docs, fy]); // eslint-disable-line react-hooks/exhaustive-deps

  // ---------- aging ----------
  const aging = useMemo(() => {
    const byClient = {};
    for (const { inv, t, status } of docs) {
      if (inv.currency !== cur || (inv.docType || "invoice") !== "invoice" || t.balance <= 0 || status === "paid") continue;
      const b = agingBucket(inv);
      const r = (byClient[inv.clientId || inv.billTo?.name] ||= { client: inv.billTo?.name, clientId: inv.clientId, current: 0, "1-30": 0, "31-60": 0, "61-90": 0, "90+": 0, total: 0 });
      r[b] += t.balance;
      r.total += t.balance;
    }
    return Object.values(byClient).sort((a, b) => b.total - a.total);
  }, [docs, cur]);

  // ---------- revenue by client ----------
  const byClient = useMemo(() => {
    const m = {};
    for (const { inv, t, sign } of docs) {
      if (inv.currency !== cur || !inFy(inv.date)) continue;
      const r = (m[inv.clientId || inv.billTo?.name] ||= { client: inv.billTo?.name, invoices: 0, billed: 0, received: 0, outstanding: 0 });
      if (sign > 0) r.invoices++;
      r.billed += sign * t.total;
      if (sign > 0) {
        r.received += t.paid;
        r.outstanding += Math.max(0, t.balance);
      }
    }
    const rows = Object.values(m).sort((a, b) => b.billed - a.billed);
    const total = rows.reduce((s, r) => s + r.billed, 0) || 1;
    return rows.map((r) => ({ ...r, share: (r.billed / total) * 100 }));
  }, [docs, cur, fy]); // eslint-disable-line react-hooks/exhaustive-deps

  // ---------- revenue by service (line categories) ----------
  const byService = useMemo(() => {
    const m = {};
    const tagName = Object.fromEntries(store.settings.tags.map((t) => [t.id, t.name]));
    for (const { inv, t, sign } of docs) {
      if (inv.currency !== cur || !inFy(inv.date)) continue;
      const clients = new Set();
      for (const l of t.lines) {
        const key = l.tag || "";
        const r = (m[key] ||= { service: tagName[key] || "Uncategorised", tag: key, lines: 0, clients: new Set(), billed: 0 });
        r.lines++;
        r.billed += sign * l.amount;
        r.clients.add(inv.clientId || inv.billTo?.name);
        clients.add(key);
      }
    }
    const rows = Object.values(m).map((r) => ({ ...r, clients: r.clients.size })).sort((a, b) => b.billed - a.billed);
    const total = rows.reduce((s, r) => s + r.billed, 0) || 1;
    return rows.map((r) => ({ ...r, share: (r.billed / total) * 100 }));
  }, [docs, cur, fy, store.settings.tags]); // eslint-disable-line react-hooks/exhaustive-deps

  // ---------- revenue by project ----------
  const byProject = useMemo(() => {
    const m = {};
    const clientName = Object.fromEntries(store.clients.map((c) => [c.id, c.name]));
    for (const { inv, t, sign } of docs) {
      if (inv.currency !== cur || !inFy(inv.date)) continue;
      for (const l of t.lines) {
        const key = l.projectId || `line:${l.description}`;
        const p = store.projects.find((x) => x.id === l.projectId);
        const r = (m[key] ||= { project: p ? [p.name, p.service].filter(Boolean).join(" ") : l.description, client: clientName[inv.clientId] || inv.billTo?.name, months: new Set(), billed: 0 });
        r.billed += sign * l.amount;
        r.months.add(inv.periodMonth || inv.date.slice(0, 7));
      }
    }
    return Object.values(m)
      .map((r) => ({ ...r, months: r.months.size, avg: r.billed / (r.months.size || 1) }))
      .sort((a, b) => b.billed - a.billed);
  }, [docs, cur, fy, store.clients, store.projects]); // eslint-disable-line react-hooks/exhaustive-deps

  // ---------- payments ----------
  const payments = useMemo(() => {
    const out = [];
    for (const { inv } of docs) {
      if (inv.currency !== cur) continue;
      for (const p of inv.payments || []) {
        if (!inFy(p.date) || p.creditNoteId) continue;
        out.push({ date: p.date, number: inv.number, id: inv.id, client: inv.billTo?.name, method: p.method, ref: p.ref || "", amount: num(p.amount), tds: num(p.tds) });
      }
    }
    return out.sort((a, b) => b.date.localeCompare(a.date));
  }, [docs, cur, fy]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!store.ready) return <Loading />;

  const views = {
    gst: {
      title: "GST summary (₹)",
      note: "Taxable values net of discounts; foreign-currency invoices converted at each invoice’s exchange rate. Credit notes are subtracted. Use this to prepare GSTR-1 / GSTR-3B.",
      columns: [
        { header: "Month", key: "month" },
        { header: "Docs", key: "count" },
        { header: "B2B taxable", key: "b2b", money: true },
        { header: "B2C taxable", key: "b2c", money: true },
        { header: "Export (LUT)", key: "export", money: true },
        { header: "CGST", key: "cgst", money: true },
        { header: "SGST", key: "sgst", money: true },
        { header: "IGST", key: "igst", money: true },
        { header: "Total tax", key: "tax", money: true },
        { header: "Credit notes", key: "credit", money: true },
      ],
      rows: gst.rows,
    },
    aging: {
      title: `Outstanding by age (${cur})`,
      note: "Days past the due date (or invoice date when there is no due date).",
      columns: [
        { header: "Client", key: "client", render: (r) => (r.clientId ? <Link className="hover:text-brand-700" href={`/invoice/clients/statement?id=${r.clientId}`}>{r.client}</Link> : r.client) },
        ...BUCKETS.map((b) => ({ header: b === "current" ? "Not yet due" : `${b} days`, key: b, money: true })),
        { header: "Total", key: "total", money: true },
      ],
      rows: aging,
    },
    clients: {
      title: `Revenue by client (${cur})`,
      columns: [
        { header: "Client", key: "client" },
        { header: "Invoices", key: "invoices" },
        { header: "Billed", key: "billed", money: true },
        { header: "Received", key: "received", money: true },
        { header: "Outstanding", key: "outstanding", money: true },
        { header: "Share", key: "share", render: (r) => `${r.share.toFixed(1)}%` },
      ],
      rows: byClient,
    },
    services: {
      title: `Revenue by service (${cur})`,
      note: "Based on each invoice line’s category (taxable amount, credit notes subtracted). Set categories on projects or lines.",
      columns: [
        {
          header: "Service",
          key: "service",
          render: (r) => (
            <span className="inline-flex items-center gap-2">
              <span className="h-2.5 w-2.5 rounded-full" style={{ background: store.settings.tags.find((t) => t.id === r.tag)?.color || "#cbd5e1" }} />
              {r.tag ? <Link className="hover:text-brand-700" href={`/invoice/invoices?tag=${r.tag}`}>{r.service}</Link> : r.service}
            </span>
          ),
        },
        { header: "Lines", key: "lines" },
        { header: "Clients", key: "clients" },
        { header: "Billed", key: "billed", money: true },
        { header: "Share", key: "share", render: (r) => `${r.share.toFixed(1)}%` },
      ],
      rows: byService,
    },
    projects: {
      title: `Revenue by project (${cur})`,
      note: "Built from invoice lines; lines added from projects are grouped per project.",
      columns: [
        { header: "Project", key: "project" },
        { header: "Client", key: "client" },
        { header: "Months billed", key: "months" },
        { header: "Billed", key: "billed", money: true },
        { header: "Avg / month", key: "avg", money: true },
      ],
      rows: byProject,
    },
    payments: {
      title: `Payments received (${cur})`,
      note: "TDS deducted by clients is shown separately – reconcile it with Form 26AS.",
      columns: [
        { header: "Date", key: "date" },
        { header: "Invoice", key: "number", render: (r) => <Link className="text-brand-700 hover:underline" href={`/invoice/invoices/view?id=${r.id}`}>{r.number}</Link> },
        { header: "Client", key: "client" },
        { header: "Method", key: "method" },
        { header: "Reference", key: "ref" },
        { header: "Amount", key: "amount", money: true },
        { header: "TDS", key: "tds", money: true },
      ],
      rows: payments,
    },
  };
  const v = views[tab];

  const exportExcel = async () => {
    const { downloadSheets } = await import("@/lib/invoice/excel");
    await downloadSheets(
      Object.entries(views).map(([k, x]) => ({
        name: TABS.find((t) => t.value === k).label,
        columns: x.columns.map((c) => ({ header: c.header, key: c.key, money: c.money, width: c.key === "client" || c.key === "project" ? 34 : 15 })),
        rows: x.rows.map((r) => ({ ...r, share: r.share != null ? Number(r.share.toFixed(1)) : undefined })),
      })),
      `reports-FY${fy}-${cur}.xlsx`
    );
    toast("Report exported", { tone: "success" });
  };

  return (
    <div>
      <PageHeader
        title="Reports"
        subtitle="Finalised invoices and credit notes only (drafts and cancelled excluded)"
        actions={
          <>
            <div className="w-36">
              <Select value={fy} onChange={(e) => setFy(e.target.value)}>
                {fys.map((f) => <option key={f} value={f}>FY {f}</option>)}
                <option value="all">All time</option>
              </Select>
            </div>
            {currencies.length > 1 && (
              <div className="w-24">
                <Select value={cur} onChange={(e) => setCurrency(e.target.value)}>
                  {currencies.map((c) => <option key={c}>{c}</option>)}
                </Select>
              </div>
            )}
            <Button variant="secondary" onClick={exportExcel}>Export all to Excel</Button>
          </>
        }
      />
      <Tabs tabs={TABS} value={tab} onChange={setTab} />
      {tab === "gst" && gst.missingFx.length > 0 && (
        <div className="mb-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-2 text-sm text-amber-900">
          {gst.missingFx.length} foreign-currency document(s) have no exchange rate, so they count as ₹0 here:{" "}
          {gst.missingFx.slice(0, 5).map((i, k) => (
            <span key={i.id}>
              {k ? ", " : ""}
              <Link className="underline" href={`/invoice/invoices/edit?id=${i.id}`}>{i.number}</Link>
            </span>
          ))}
          {gst.missingFx.length > 5 ? "…" : ""}. Open each and use “Fetch” next to the exchange rate.
        </div>
      )}
      <Card title={v.title}>
        {v.note && <div className="mb-3 text-xs text-slate-500">{v.note}</div>}
        <Table columns={v.columns} rows={v.rows} foot={tab !== "payments" || v.rows.length > 0} />
        {tab === "aging" && aging.length > 0 && (
          <div className="mt-3 text-sm text-slate-600">
            Total outstanding: <b>{fmtMoney(aging.reduce((s, r) => s + r.total, 0), cur)}</b>
          </div>
        )}
      </Card>
    </div>
  );
}
